import { Auth } from '@core/auth';

import PaymentFrequencyEnum from '@constants/enums/payment/frequency';

import ReportApi from '@packages/reports/api';

import i18n from '@plugins/i18n';

import { Collection, Adviser } from '@/models';

export class ReportCashFeesCollection extends Collection {
    static type = 'ReportCashFeesCollection';
    static endpoint = new ReportApi().methods.getCashFees;

    static fields() {
        return {
            ...super.fields(),

            adviser_id: this.string(null).nullable(),
            adviser: this.belongsTo(Adviser, 'adviser_id')
        };
    }

    get preset_filters() {
        let filters = {};

        if (this.adviser_id) {
            filters.adviser_id = this.filterOperator('is') + ':' + this.adviser_id;
        }

        return filters;
    }

    get money_format_options() {
        return {
            decimals: 2,
            abbreviate: false
        };
    }

    isStandingOrderEnabled(standingOrder) {
        return standingOrder !== PaymentFrequencyEnum.NO_AUTO;
    }

    reserveDisplayValue({ item, value }) {
        if (!this.isStandingOrderEnabled(item?.standing_order)) {
            return value;
        }

        if (value && typeof value === 'object' && 'amount' in value) {
            return {
                ...value,
                amount: 0
            };
        }

        return 0;
    }

    get default_headers() {
        return [
            'adviser',
            'adviser_ref',
            'investor',
            'investor_ref',
            'primary_contact',
            'fees_paid_platform',
            'fees_paid_initial_adviser',
            'fees_paid_ongoing_adviser',
            'fees_paid_total',
            'fees_due_platform',
            'fees_due_initial_adviser',
            'fees_due_ongoing_adviser',
            'fees_due_total',
            'reserve_platform',
            'reserve_adviser',
            'reserve_total',
            'cleared_funds_required_proposals',
            'cleared_funds_fees',
            'cleared_funds_total',
            'cash_james_brearley',
            'cash_woodside',
            'cash_total',
            'transfer_on',
            'dividends',
            'interest',
            'excess_cash_period',
            'standing_order'
        ];
    }

    get headers() {
        return [
            this.createHeader('adviser', i18n.t('adviser'), {
                sortable: true,
                filterable: true,
                width: '14rem',
                active: !this.adviser_id && Auth().is_gi
            }),
            this.createHeader('adviser_ref', i18n.t('adviser') + ' ' + i18n.t('ref'), {
                sortable: true,
                filterable: true,
                width: '9rem',
                active: !this.adviser_id && Auth().is_gi
            }),
            this.createHeader('investor', i18n.t('investor'), {
                sortable: true,
                filterable: true,
                width: '14rem',
                active: !this.investor_id && Auth().is_adviser
            }),
            this.createHeader('investor_ref', i18n.t('investor') + ' ' + i18n.t('ref'), {
                sortable: true,
                filterable: true,
                width: '9rem',
                active: !this.investor_id && Auth().is_adviser
            }),
            this.createHeader('primary_contact', i18n.t('primary_contact'), {
                sortable: true,
                filterable: true,
                width: '11rem'
            }),
            //
            this.createHeader('fees_paid_platform', i18n.t('fees_paid_platform'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '8rem'
            }),
            this.createHeader('fees_paid_initial_adviser', i18n.t('fees_paid_initial_adviser'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '8rem'
            }),
            this.createHeader('fees_paid_ongoing_adviser', i18n.t('fees_paid_ongoing_adviser'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '9rem'
            }),
            this.createHeader('fees_paid_total', i18n.t('fees_paid_total'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '8rem'
            }),
            //
            this.createHeader('fees_due_platform', i18n.t('fees_due_platform'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '8em'
            }),
            this.createHeader('fees_due_initial_adviser', i18n.t('fees_due_initial_adviser'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '8rem'
            }),
            this.createHeader('fees_due_ongoing_adviser', i18n.t('fees_due_ongoing_adviser'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '9rem'
            }),
            this.createHeader('fees_due_total', i18n.t('fees_due_total'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '9rem'
            }),
            //
            this.createHeader('reserve_platform', i18n.t('reserve_platform'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                transform: data => this.reserveDisplayValue(data),
                tooltip: i18n.t('standing_order_reserve_override'),
                width: '8rem'
            }),
            this.createHeader('reserve_adviser', i18n.t('reserve_adviser'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                transform: data => this.reserveDisplayValue(data),
                tooltip: i18n.t('standing_order_reserve_override'),
                width: '8rem'
            }),
            this.createHeader('reserve_total', i18n.t('reserve_total'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                transform: data => this.reserveDisplayValue(data),
                tooltip: i18n.t('standing_order_reserve_override'),
                width: '7rem'
            }),
            //
            this.createHeader('cleared_funds_required_proposals', i18n.t('cleared_funds_required_proposals'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '12rem'
            }),
            this.createHeader('cleared_funds_fees', i18n.t('cleared_funds_fees'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '8rem'
            }),
            this.createHeader('cleared_funds_total', i18n.t('cleared_funds_total'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '8rem'
            }),
            //
            this.createHeader('cash_james_brearley', i18n.t('cash_james_brearley'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '9rem'
            }),
            this.createHeader('cash_woodside', i18n.t('cash_woodside'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '8rem'
            }),
            this.createHeader('cash_total', i18n.t('cash_total'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '8rem'
            }),
            //
            this.createHeader('transfer_on', i18n.t('transfer_on'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '9rem'
            }),
            this.createHeader('dividends', i18n.t('dividends'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '9rem'
            }),
            this.createHeader('interest', i18n.t('interest'), {
                sortable: true,
                format: 'money',
                formatOptions: this.money_format_options,
                width: '9rem'
            }),
            this.createHeader('excess_cash_period', i18n.t('excess_cash_period'), {
                sortable: true,
                format: 'enum',
                enum: PaymentFrequencyEnum,
                enumA11yKey: 'enums.payment_frequency',
                width: '9rem'
            }),
            this.createHeader('standing_order', i18n.t('standing_order'), {
                sortable: true,
                format: 'enum',
                enum: PaymentFrequencyEnum,
                enumA11yKey: 'enums.payment_frequency',
                width: '10rem'
            })
        ];
    }
}

export default ReportCashFeesCollection;
